/**
 * The catalogue registry (R-FN-5, ADR 0112): every feature area, its routes and its settings, as
 * data both hosts (the Node hub and Orgus Cloud) and the web console read. A module adds its
 * routes to its own area() block and its settings with setting(); catalog.ts turns this into the
 * layered catalog.index and catalog.describe answers. apps/hub/src/ext/catalog.test.ts fails when
 * a hub module serves a route that is missing here (waivers in catalog-waivers.json).
 *
 * Ops are 'METHOD /path' with ':name' path parameters. Browser-safe and dependency-free.
 */

export type CatalogValueType = 'string' | 'number' | 'integer' | 'boolean';

export interface CatalogSetting {
  /** '<area>.<field path>', e.g. 'scheduler.headroom.maxLoad'. */
  key: string;
  type: CatalogValueType;
  summary: string;
  default: string | number | boolean | null;
  nullable?: boolean;
  enum?: readonly (string | number)[];
  min?: number;
  max?: number;
  /** Where the current value is read: a GET route and the dotted field in its answer. */
  read: { path: string; field: string };
  /** Where it is changed: the route, and the dotted field of the partial body it takes. */
  write: { method: 'PUT' | 'PATCH'; path: string; field: string };
}

export interface CatalogArea {
  name: string;
  /** One line, a few words: the whole index is meant to stay under ~600 tokens. */
  purpose: string;
  ops: readonly string[];
  settings: readonly CatalogSetting[];
}

const area = (name: string, purpose: string, ops: readonly string[], settings: readonly CatalogSetting[] = []): CatalogArea => ({ name, purpose, ops, settings });

type SettingIn = Omit<CatalogSetting, 'key' | 'read' | 'write'> & { field: string };
const settingsOf =
  (areaName: string, read: string, readRoot: string, write: { method: 'PUT' | 'PATCH'; path: string }) =>
  (...list: SettingIn[]): CatalogSetting[] =>
    list.map(({ field, ...s }) => ({ ...s, key: `${areaName}.${field}`, read: { path: read, field: readRoot ? `${readRoot}.${field}` : field }, write: { ...write, field } }));

const COSTS_SETTINGS = settingsOf('costs', '/api/costs', '', { method: 'PUT', path: '/api/costs/settings' })(
  { field: 'billing', type: 'string', enum: ['subscription', 'api'], default: 'subscription', summary: 'How Claude Code is paid for: subscription or API.' },
);
const NOTIFY_SETTINGS = settingsOf('notify', '/api/notify', 'settings', { method: 'PUT', path: '/api/notify/settings' })(
  { field: 'digestAt', type: 'string', nullable: true, default: '18:00', summary: 'Local time of the daily digest, HH:MM; null turns it off.' },
  { field: 'batchMinutes', type: 'integer', nullable: true, min: 5, max: 1440, default: null, summary: 'Batch low-urgency events this often; null waits for the digest.' },
  { field: 'publicUrl', type: 'string', nullable: true, default: null, summary: 'Base address links in messages point to.' },
  { field: 'timeZone', type: 'string', nullable: true, default: null, summary: 'IANA time zone for quiet hours and the digest; null: the hub\'s.' },
);
const AIJOBS_SETTINGS = settingsOf('aijobs', '/api/aijobs/settings', '', { method: 'PATCH', path: '/api/aijobs/settings' })(
  { field: 'model', type: 'string', default: '', summary: "Model for AI jobs; empty: the machine's default." },
  { field: 'maxBudgetUsd', type: 'number', min: 0.01, max: 100, default: 2, summary: 'Most a job may cost, USD.' },
  { field: 'timeoutMs', type: 'integer', min: 30_000, max: 3_600_000, default: 600_000, summary: 'A job stops after this many ms.' },
);
const SCHEDULER_SETTINGS = settingsOf('scheduler', '/api/scheduler', 'settings', { method: 'PATCH', path: '/api/scheduler/settings' })(
  { field: 'limits.perProject', type: 'integer', nullable: true, min: 1, max: 1000, default: null, summary: 'Sessions at once per project; null: no limit.' },
  { field: 'limits.perPerson', type: 'integer', nullable: true, min: 1, max: 1000, default: null, summary: 'Sessions at once per person; null: no limit.' },
  { field: 'headroom.maxLoad', type: 'number', min: 0.1, max: 1, default: 0.9, summary: 'A machine busier than this (0..1) takes no new work.' },
  { field: 'headroom.minFreeMemory', type: 'number', min: 0, max: 0.9, default: 0.08, summary: 'Free memory floor (0..0.9) for new work.' },
  { field: 'defaultOffline', type: 'string', enum: ['wait', 'reschedule'], default: 'wait', summary: 'When its machine is offline a mission waits or moves.' },
  { field: 'utcOffsetMinutes', type: 'integer', nullable: true, min: -840, max: 840, default: null, summary: 'Minutes east of UTC for time rules; null: the hub\'s.' },
);

export const CATALOG_AREAS: readonly CatalogArea[] = [
  area('acp-registry', 'Agent kinds from the ACP registry', [
    'GET /api/acp-registry',
    'POST /api/acp-registry/refresh',
    'PUT /api/acp-registry/settings',
  ]),
  area('agent-environment', 'Tools and versions on each machine', [
    'GET /api/agent-environment',
    'POST /api/agent-environment/refresh',
  ]),
  area('agent-install', 'Install commands for the node agent', [
    'GET /api/agent-install',
    'POST /api/agent-install',
    'PUT /api/agent-install/settings',
  ]),
  area('agent-usage', 'Agent plan usage and limits', [
    'PUT /api/agent-usage/settings',
  ]),
  area('ai', 'AI API tokens and activity', [
    'GET /api/ai/capabilities',
    'GET /api/ai/connect',
    'POST /api/ai/invoke',
    'GET /api/ai/state',
  ]),
  area('aijobs', 'AI jobs: imports, splits, drafts', [
    'GET /api/aijobs',
    'DELETE /api/aijobs/:p',
    'GET /api/aijobs/:p',
    'POST /api/aijobs/:p/cancel',
    'GET /api/aijobs/settings',
    'PATCH /api/aijobs/settings',
  ], AIJOBS_SETTINGS),
  area('approvals', 'Approval requests and decisions', [
    'GET /api/approvals',
    'POST /api/approvals/:p',
    'POST /api/approvals/:p/dismiss',
    'POST /api/approvals/:p/hand-back',
    'POST /api/approvals/:p/resume',
    'GET /api/approvals/answerer',
    'PUT /api/approvals/answerer',
    'POST /api/approvals/batch',
    'GET /api/approvals/delegates',
    'GET /api/approvals/delegates[/:p]',
    'DELETE /api/approvals/delegates/:p',
    'PUT /api/approvals/delegates/:p',
    'GET /api/approvals/expiry',
    'PUT /api/approvals/expiry',
    'POST /api/approvals/session-allows/revoke',
  ]),
  area('asks', 'Questions waiting for a person', [
    'GET /api/asks',
    'POST /api/asks/:p/dismiss',
    'POST /api/asks/:p/snooze',
    'POST /api/asks/:p/undo',
    'POST /api/asks/dismiss',
  ]),
  area('assistant', 'The Orgus Assistant (person only)', [
    'GET /api/assistant',
    'POST /api/assistant/approvals/:p',
    'POST /api/assistant/conversations',
    'DELETE /api/assistant/conversations/:p',
    'GET /api/assistant/conversations/:p',
    'PATCH /api/assistant/conversations/:p',
    'POST /api/assistant/conversations/:p/messages',
    'POST /api/assistant/conversations/:p/stop',
    'PATCH /api/assistant/settings',
  ]),
  area('attachments', 'Files attached to items', [
    'GET /api/attachments',
    'POST /api/attachments',
    'DELETE /api/attachments/:p',
    'GET /api/attachments/:p',
    'PATCH /api/attachments/:p',
    'GET /api/attachments/:p/raw',
    'GET /api/attachments/:p/thumb',
    'PUT /api/attachments/:p/thumb',
    'GET /api/attachments/caller',
  ]),
  area('audit-ops', 'Audit search, export, retention', [
    'GET /api/audit/export',
    'GET /api/audit/query',
    'GET /api/audit/retention',
    'PUT /api/audit/retention',
    'POST /api/audit/retention/run',
    'GET /api/audit/verify',
    'POST /api/ops/snapshot',
    'GET /api/security',
    'POST /api/security/alerts/:p/ack',
    'PUT /api/security/rules',
  ]),
  area('auth', 'Sign-in, members, roles, tokens', [
    'GET /api/auth/:p/callback',
    'GET /api/auth/:p/start',
    'POST /api/auth/:p/start',
    'POST /api/auth/device',
    'POST /api/auth/device/poll',
    'GET /api/auth/github/callback',
    'GET /api/auth/github/start',
    'DELETE /api/auth/identities/:p',
    'POST /api/auth/identities/:p/confirm',
    'POST /api/auth/identities/link',
    'GET /api/auth/identities/mine',
    'POST /api/auth/invites',
    'DELETE /api/auth/invites/:p',
    'POST /api/auth/invites/link',
    'POST /api/auth/invites/peek',
    'POST /api/auth/join-rules',
    'DELETE /api/auth/join-rules/:p',
    'GET /api/auth/members',
    'DELETE /api/auth/members/:p',
    'PATCH /api/auth/members/:p',
    'GET /api/auth/providers',
    'POST /api/auth/removed/:p/allow',
    'POST /api/auth/rules',
    'DELETE /api/auth/rules/:p/:p',
    'POST /api/auth/sessions/end-mine',
    'GET /api/auth/sign-in',
    'PUT /api/auth/sign-in/policy',
    'PUT /api/auth/sign-in/policy[',
    'POST /api/auth/sign-out',
    'GET /api/auth/tokens',
    'POST /api/auth/tokens',
    'DELETE /api/auth/tokens/:p',
    'GET /api/me',
  ]),
  area('ci', 'CI pipelines and runs', [
    'GET /api/ci',
    'POST /api/ci/connections',
    'DELETE /api/ci/connections/:p',
    'PATCH /api/ci/connections/:p',
    'POST /api/ci/connections/:p/sync',
    'POST /api/ci/dispatch',
    'POST /api/ci/gates/:p',
    'POST /api/ci/hooks/:p',
    'GET /api/ci/runs',
    'GET /api/ci/runs/:p',
    'POST /api/ci/runs/:p/cancel',
    'GET /api/ci/runs/:p/logs/:p',
    'POST /api/ci/runs/:p/rerun',
  ]),
  area('cloud', 'Cloud accounts and VMs', [
    'GET /api/cloud',
    'DELETE /api/cloud/aws',
    'POST /api/cloud/aws/adopt',
    'POST /api/cloud/aws/connect',
    'GET /api/cloud/aws/instances',
    'POST /api/cloud/aws/setup',
    'POST /api/cloud/aws/test',
    'POST /api/cloud/vms',
    'DELETE /api/cloud/vms/:p',
    'POST /api/cloud/vms/:p/:p',
    'PUT /api/cloud/vms/:p/rules',
  ]),
  area('comments', 'Comments on items', [
    'GET /api/comments',
    'POST /api/comments',
    'DELETE /api/comments/:p',
    'PATCH /api/comments/:p',
  ]),
  area('control', 'Session control switch', [
    'GET /api/control',
    'POST /api/control/:p',
    'POST /api/control/:p/message',
  ]),
  area('core', 'State, machines, actions, join tokens, audit', [
    'POST /api/actions',
    'GET /api/audit',
    'GET /api/health',
    'POST /api/join-tokens',
    'DELETE /api/logs/:p',
    'POST /api/logs/:p',
    'DELETE /api/machines/:p',
    'PATCH /api/machines/:p',
    'DELETE /api/session',
    'POST /api/session',
    'GET /api/signin-hint',
    'GET /api/state',
    'GET /api/stream',
    'GET /api/trust',
    'GET /api/trust/audit/verify',
    'DELETE /api/trust/freeze',
    'POST /api/trust/freeze',
    'DELETE /api/trust/nodes/:p/key',
    'POST /api/trust/nodes/:p/key',
    'POST /api/trust/nodes/:p/release',
    'DELETE /api/trust/projects/:p/pause',
    'POST /api/trust/projects/:p/pause',
    'PUT /api/trust/settings',
  ]),
  area('costs', 'Spend, budgets, billing mode', [
    'GET /api/costs',
    'PUT /api/costs/accounts/:p',
    'POST /api/costs/budgets',
    'DELETE /api/costs/budgets/:p',
    'PUT /api/costs/budgets/:p',
    'GET /api/costs/export.csv',
    'GET /api/costs/pricing',
    'PUT /api/costs/settings',
  ], COSTS_SETTINGS),
  area('diff', 'Diffs of sessions and branches', [
    'GET /api/sessions/:p/diff',
  ]),
  area('discipline', 'Standards, design rules, checks', [
    'GET /api/ext/discipline',
    'POST /api/ext/discipline/baseline',
    'DELETE /api/ext/discipline/baseline/:p',
    'POST /api/ext/discipline/check',
    'POST /api/ext/discipline/drift',
    'PUT /api/ext/discipline/drift/schedule',
    'POST /api/ext/discipline/dryrun',
    'POST /api/ext/discipline/onboard',
    'POST /api/ext/discipline/packs',
    'POST /api/ext/discipline/pins',
    'DELETE /api/ext/discipline/pins/:p[',
    'DELETE /api/ext/discipline/rulebook/:p',
    'PUT /api/ext/discipline/rulebook/:p',
    'POST /api/ext/discipline/sendbacks',
    'POST /api/ext/discipline/sendbacks/:p/cancel',
    'POST /api/ext/discipline/waivers',
    'DELETE /api/ext/discipline/waivers/:p',
    'GET /api/standards/effective',
    'GET /api/standards/enforcement',
    'PUT /api/standards/enforcement',
    'GET /api/standards/provenance',
    'GET /api/standards/layers',
    'PUT /api/standards/layers',
  ]),
  area('finance', 'Spend reports, subscriptions, providers', [
    'GET /api/finance',
    'POST /api/finance/entries',
    'DELETE /api/finance/entries/:p',
    'PUT /api/finance/entries/:p',
    'DELETE /api/finance/entries/:p/receipt',
    'POST /api/finance/entries/:p/receipt',
    'PUT /api/finance/entries/:p/receipt',
    'GET /api/finance/entries/:p/receipt[',
    'GET /api/finance/export.csv',
    'POST /api/finance/import',
    'GET /api/finance/report',
    'PUT /api/finance/settings',
    'POST /api/finance/subscriptions',
    'DELETE /api/finance/subscriptions/:p',
    'PUT /api/finance/subscriptions/:p',
    'POST /api/finance/sync/:p',
    'POST /api/finance/sync/billing',
  ]),
  area('fleet', 'Machine groups, updates, rollouts', [
    'GET /api/fleet',
    'GET /api/fleet/download/:p',
    'GET /api/fleet/downloads',
    'GET /api/fleet/downloads/:p',
    'POST /api/fleet/inventory',
    'PUT /api/fleet/machines/:p',
    'POST /api/fleet/machines/:p/health',
    'POST /api/fleet/machines/:p/update',
    'PUT /api/fleet/policy',
    'POST /api/fleet/policy/pause',
    'POST /api/fleet/policy/resume',
    'POST /api/fleet/rollouts',
    'POST /api/fleet/rollouts/:p/cancel',
    'POST /api/fleet/rollouts/:p/promote',
    'POST /api/fleet/versions',
  ]),
  area('flows', 'Saved workflows', [
    'GET /api/flows',
    'POST /api/flows',
    'DELETE /api/flows/:p',
    'GET /api/flows/:p',
    'PUT /api/flows/:p',
    'GET /api/flows/:p/revisions',
    'GET /api/flows/:p/revisions/:p',
  ]),
  area('forecasts', 'Calibrated forecasts', [
    'GET /api/forecasts',
    'GET /api/forecasts/calibration',
  ]),
  area('history', 'State history and retention', [
    'GET /api/history/retention',
    'PUT /api/history/retention',
    'GET /api/history/series',
    'GET /api/history/sessions',
  ]),
  area('landing', 'Landing queues and environments', [
    'GET /api/landing',
    'GET /api/landing/config/:p',
    'PUT /api/landing/config/:p',
    'POST /api/landing/environments/:p/:p/:p',
    'POST /api/landing/environments/:p/:p/deploy',
    'POST /api/landing/environments/:p/:p/freezes',
    'DELETE /api/landing/environments/:p/:p/freezes/:p',
    'POST /api/landing/environments/:p/:p/promote',
    'POST /api/landing/environments/:p/:p/rollback',
    'POST /api/landing/landings',
    'POST /api/landing/landings/:p/cancel',
    'POST /api/landing/landings/:p/retry',
    'POST /api/landing/queues/:p/:p',
    'POST /api/landing/queues/:p/consent',
    'POST /api/landing/queues/:p/pause',
    'POST /api/landing/queues/:p/reorder',
  ]),
  area('launch-accounts', 'Accounts and pools sessions launch with', [
    'GET /api/launch-accounts',
    'POST /api/launch-accounts',
    'DELETE /api/launch-accounts/:p',
    'PUT /api/launch-accounts/:p',
    'PUT /api/launch-accounts/:p/fallback',
    'DELETE /api/launch-accounts/:p/folders/:p',
    'PUT /api/launch-accounts/:p/folders/:p',
    'DELETE /api/launch-accounts/:p/limit',
    'DELETE /api/launch-accounts/:p/sign-in/:p',
    'POST /api/launch-accounts/:p/sign-in/:p',
    'POST /api/launch-accounts/:p/sign-in/:p/code',
    'POST /api/launch-accounts/:p/test',
    'PUT /api/launch-accounts/defaults/:p',
    'PUT /api/launch-accounts/pools/:p',
    'POST /api/launch-accounts/usage/refresh',
  ]),
  area('licensing', 'Plan and entitlements', [
    'GET /api/licensing',
    'POST /api/licensing/ask',
    'POST /api/licensing/licence',
  ]),
  area('lifecycle', 'Hub restart and update', [
    'GET /api/hub/lifecycle',
    'POST /api/hub/restart',
    'POST /api/ops/restart',
  ]),
  area('live', 'Live views', [
    'GET /api/live',
    'GET /api/live/room',
  ]),
  area('missions', 'Backlog, missions, proposals', [
    'GET /api/missions',
    'POST /api/missions',
    'DELETE /api/missions/:p',
    'GET /api/missions/:p',
    'PATCH /api/missions/:p',
    'POST /api/missions/:p/continue-fresh',
    'POST /api/missions/:p/pause',
    'POST /api/missions/:p/resume',
    'POST /api/missions/:p/start',
    'POST /api/missions/:p/stop',
    'POST /api/missions/ai',
    'GET /api/missions/autopilot',
    'GET /api/missions/autopilot/:p',
    'PUT /api/missions/autopilot/:p',
    'POST /api/missions/autopilot/groomed/:p/undo',
    'GET /api/missions/continuation',
    'POST /api/missions/import',
    'POST /api/missions/items',
    'DELETE /api/missions/items/:p',
    'GET /api/missions/items/:p',
    'PATCH /api/missions/items/:p',
    'POST /api/missions/items/:p/criteria/:p',
    'GET /api/missions/items/:p/history',
    'GET /api/missions/items/:p/review',
    'POST /api/missions/items/bulk',
    'POST /api/missions/items/bulk-delete',
    'POST /api/missions/items/bulk-delete:',
    'PATCH /api/missions/keys/:p',
    'POST /api/missions/links',
    'DELETE /api/missions/links/:p',
    'PUT /api/missions/pause',
    'GET /api/missions/proposals/:p',
    'PATCH /api/missions/proposals/:p',
    'POST /api/missions/proposals/:p/apply',
    'POST /api/missions/proposals/:p/discard',
    'POST /api/missions/sequences',
    'POST /api/missions/sequences/:p/stop',
    'POST /api/missions/signals/:p/accept',
    'POST /api/missions/signals/:p/dismiss',
    'POST /api/missions/signals/scan',
    'POST /api/missions/views',
    'DELETE /api/missions/views/:p',
  ]),
  area('notify', 'Notifications, channels, quiet hours', [
    'GET /api/notify',
    'GET /api/notify/act',
    'POST /api/notify/act',
    'POST /api/notify/channels',
    'DELETE /api/notify/channels/:p',
    'PATCH /api/notify/channels/:p',
    'POST /api/notify/digest',
    'POST /api/notify/escalations/:p/ack',
    'GET /api/notify/inbox',
    'POST /api/notify/members',
    'DELETE /api/notify/members/:p',
    'PATCH /api/notify/members/:p',
    'POST /api/notify/members/import',
    'POST /api/notify/rotas',
    'DELETE /api/notify/rotas/:p',
    'PATCH /api/notify/rotas/:p',
    'POST /api/notify/seen',
    'PUT /api/notify/settings',
    'DELETE /api/notify/smtp',
    'PUT /api/notify/smtp',
    'POST /api/notify/smtp/test',
    'DELETE /api/notify/subscriptions',
    'POST /api/notify/subscriptions',
    'GET /api/notify/summary',
    'POST /api/notify/test',
  ], NOTIFY_SETTINGS),
  area('onboarding', 'First-run setup', [
    'GET /api/onboarding',
    'POST /api/onboarding',
    'GET /api/onboarding/reach',
  ]),
  area('ops-health', 'Hub health checks', [
    'GET /api/ops-health',
  ]),
  area('pairing', 'Node pairing and trust', [
    'GET /api/pair/requests',
    'POST /api/pair/requests',
    'DELETE /api/pair/requests/:p',
    'GET /api/pair/requests/:p',
    'POST /api/pair/requests/:p/approve',
    'POST /api/pair/requests/:p/claim',
    'POST /api/pair/requests/:p/deny',
    'POST /api/pair/requests/:p/peek',
    'GET /api/pair/requests/:p/poll',
    'GET /api/pair/tickets',
    'POST /api/pair/tickets',
    'DELETE /api/pair/tickets/:p',
  ]),
  area('policy', 'Rules, freezes, overrides', [
    'GET /api/policy',
    'GET /api/policy/data',
    'PUT /api/policy/data',
    'GET /api/policy/diff',
    'GET /api/policy/effective',
    'POST /api/policy/evaluate',
    'POST /api/policy/freezes',
    'DELETE /api/policy/freezes/:p',
    'PUT /api/policy/freezes/:p',
    'GET /api/policy/history',
    'GET /api/policy/landing',
    'GET /api/policy/launches',
    'POST /api/policy/rollback',
    'POST /api/policy/rules',
    'DELETE /api/policy/rules/:p',
    'PUT /api/policy/rules/:p',
    'PUT /api/policy/settings',
    'POST /api/policy/simulate',
    'GET /api/policy/versions',
    'GET /api/policy/versions/:p',
  ]),
  area('project-protocol', 'Per-project protocol files', [
    'GET /api/project-protocol',
    'POST /api/project-protocol/actions/:p/cancel',
    'POST /api/project-protocol/actions/:p/confirm',
    'POST /api/project-protocol/actions/run',
    'POST /api/project-protocol/dispatch',
    'PUT /api/project-protocol/settings',
    'POST /api/project-protocol/yields/:p/start-now',
  ]),
  area('scheduler', 'Queue, placement, limits, pools', [
    'GET /api/scheduler',
    'POST /api/scheduler/agents/:p/:p/test',
    'PATCH /api/scheduler/nodes/:p',
    'POST /api/scheduler/nodes/:p/drain',
    'POST /api/scheduler/nodes/:p/resume',
    'POST /api/scheduler/pools',
    'DELETE /api/scheduler/pools/:p',
    'PATCH /api/scheduler/pools/:p',
    'PUT /api/scheduler/projects/:p',
    'POST /api/scheduler/queue',
    'DELETE /api/scheduler/queue/:p',
    'POST /api/scheduler/queue/:p/retry',
    'POST /api/scheduler/rules',
    'DELETE /api/scheduler/rules/:p',
    'PATCH /api/scheduler/settings',
    'POST /api/scheduler/start',
  ], SCHEDULER_SETTINGS),
  area('secrets', 'Secret names and leases', [
    'GET /api/secrets',
    'POST /api/secrets',
    'DELETE /api/secrets/:p',
    'PUT /api/secrets/:p',
    'POST /api/secrets/leases/:p/revoke',
    'POST /api/secrets/rotate',
  ]),
  area('session-control', 'Messages, claims, session hooks', [
    'DELETE /api/sessions/:p/claim',
    'POST /api/sessions/:p/claim',
    'GET /api/sessions/:p/detail',
    'GET /api/sessions/:p/events',
    'POST /api/sessions/:p/input',
    'POST /api/sessions/:p/landed',
    'GET /api/sessions/:p/messages',
    'POST /api/sessions/:p/messages',
    'POST /api/sessions/:p/wake',
  ]),
  area('stack', 'Detected project stacks', [
    'GET /api/stack',
    'POST /api/stack/refresh',
  ]),
  area('access', 'Roles, grants by project or team, approval policies, access review', [
    'GET /api/rbac',
    'POST /api/rbac/roles',
    'PATCH /api/rbac/roles/:p',
    'DELETE /api/rbac/roles/:p',
    'POST /api/rbac/bindings',
    'DELETE /api/rbac/bindings/:p',
    'POST /api/rbac/policies',
    'PATCH /api/rbac/policies/:p',
    'DELETE /api/rbac/policies/:p',
    'POST /api/rbac/explain',
    'GET /api/rbac/review',
  ]),
  area('teams', 'Workspaces, teams, assignments', [
    'DELETE /api/presence',
    'POST /api/presence',
    'GET /api/teams',
    'POST /api/teams/assign',
    'DELETE /api/teams/assignments/:p',
    'POST /api/teams/assignments/:p/ack',
    'POST /api/teams/claim',
    'PUT /api/teams/org',
    'PUT /api/teams/ownership/:p',
    'GET /api/teams/prefs',
    'PUT /api/teams/prefs',
    'POST /api/teams/route',
    'PUT /api/teams/routing',
    'POST /api/teams/teams',
    'DELETE /api/teams/teams/:p',
    'PATCH /api/teams/teams/:p',
    'POST /api/teams/workspaces',
    'DELETE /api/teams/workspaces/:p',
    'PATCH /api/teams/workspaces/:p',
  ]),
  area('templates', 'Session and mission templates', [
    'GET /api/templates',
    'POST /api/templates',
    'DELETE /api/templates/:p',
    'GET /api/templates/:p',
    'PATCH /api/templates/:p',
    'POST /api/templates/:p/start',
  ]),
  area('term', 'Terminals on machines', [
    'GET /api/term',
    'POST /api/term',
    'DELETE /api/term/:p',
    'GET /api/term/:p',
    'GET /api/term/:p/cast',
    'POST /api/term/:p/control',
  ]),
  area('views', 'Saved views', [
    'GET /api/ext/views',
    'POST /api/ext/views',
    'DELETE /api/ext/views/:p',
  ]),
  area('watch', 'Incidents and fixes', [
    'GET /api/watch',
    'POST /api/watch/:p/ack',
    'POST /api/watch/:p/act',
    'POST /api/watch/:p/dismiss',
    'POST /api/watch/:p/drop',
    'POST /api/watch/:p/fixed',
    'POST /api/watch/:p/fixing',
    'POST /api/watch/:p/keep',
  ]),
];

export function catalogArea(name: string): CatalogArea | undefined {
  return CATALOG_AREAS.find((a) => a.name === name);
}

export const CATALOG_SETTINGS: readonly CatalogSetting[] = CATALOG_AREAS.flatMap((a) => a.settings);

export function settingByKey(key: string): CatalogSetting | undefined {
  return CATALOG_SETTINGS.find((s) => s.key === key);
}
